import { stageColor, recruitingStageColors } from "../../utils/pipelineStages";
// src/components/sidebar-widgets/StagesCard.jsx
import React from "react";
import { Box, Typography, Divider, TextField, MenuItem, IconButton } from "@mui/material";
import { Calendar, ArrowLeft2, ArrowRight2 } from "iconsax-react";
import {
  SIDEBAR_WIDGET_BORDER_COLOR,
  SIDEBAR_WIDGET_CARD_SX,
  SIDEBAR_WIDGET_TITLE_SX,
} from "./sidebarWidgetStyles";

const BORDER = SIDEBAR_WIDGET_BORDER_COLOR;
const CARD_BG = "#FFFFFF";
const TEXT = "#111827";
const MUTED = "#94A3B8";
const LABEL = "#6B7280";
const PURPLE = "#9C4FFF";
const SOFT_BG = "#F7F8FF";

function Chip({ text, color = PURPLE, bg = "rgba(156,79,255,0.10)" }) {
  return (
    <Box
    
      sx={{
        
        px: 1,
        py: 0.35,
        borderRadius: 999,
        background: bg,
        color,
        fontSize: 11,
        fontWeight: 400,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </Box>
  );
}

function LogoDot({ color = PURPLE }) {
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
        flex: "0 0 auto",
      }}
    />
  );
}

function StageItem({
  item,
  onViewDetails,
}) {
  return (
    <Box sx={{ py: 1.5,  }}>
      {/* top row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Calendar size={16} color={MUTED} variant="Linear" />
          <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 400 }}>
            {item.dateText}
          </Typography>
        </Box>

        {item.badge ? (
          <Chip
            text={item.badge.text}
            color={recruitingStageColors(item.badge.text)?.color || item.badge.color}
            bg={recruitingStageColors(item.badge.text)?.activeBackground || item.badge.bg}
          />
        ) : (
          <Box />
        )}
      </Box>

      {/* stage line */}
      <Typography sx={{ fontSize: 12, color: stageColor(item.stage), fontWeight: 400, mb: 1 }}>
        Stage: {item.stage}
      </Typography>

      {/* details grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          rowGap: 0.85,
          columnGap: 2,
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 700 }}>Job:</Typography>
        <Typography sx={{ fontSize: 12, color: TEXT, fontWeight: 400, justifySelf: "end" }}>
          {item.job}
        </Typography>

        <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 700 }}>Company:</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, justifySelf: "end" }}>
          <LogoDot color={item.companyDot || PURPLE} />
          <Typography sx={{ fontSize: 12, color: TEXT, fontWeight: 400 }}>
            {item.company}
          </Typography>
        </Box>

        <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 700 }}>Hiring Manager:</Typography>
        <Typography sx={{ fontSize: 12, color: TEXT, fontWeight: 400, justifySelf: "end" }}>
          {item.hiringManager}
        </Typography>

        <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 700 }}>Team Member:</Typography>
        <Typography sx={{ fontSize: 12, color: TEXT, fontWeight: 400, justifySelf: "end" }}>
          {item.teamMember}
        </Typography>
      </Box>

      {/* view details */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.25 }}>
        <Typography
          onClick={() => onViewDetails?.(item)}
          sx={{
            fontSize: 12,
            color: PURPLE,
            fontWeight: 400,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          View details
        </Typography>
      </Box>
    </Box>
  );
}

export default function StagesCard({
  title = "Stages",
  filterValue = "all",
  resultsText = "0 results",
  items = [],
  stageOptions = [],
  loading = false,
  onFilterChange,
  onViewDetails,
  onPrev,
  onNext,
  onViewAll,
  sx = {},
}) {
  return (
    <Box
      sx={{
        ...SIDEBAR_WIDGET_CARD_SX,
        background: CARD_BG,
        mb: "28px",
        ...sx,
      }}
    >
      {/* header */}
      <Box sx={{ px: 4, pt: 4, pb: 2 }}>
        <Typography sx={SIDEBAR_WIDGET_TITLE_SX}>
          {title}
        </Typography>
      </Box>

      {/* filter */}
      <Box sx={{ px: 4, pb: 2 }}>
        <TextField
          select
          size="small"
          value={filterValue}
          onChange={(e) => onFilterChange?.(e.target.value)}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              height: 34,
              borderRadius: 2,
              background: "#fff",
              "& fieldset": { borderColor: BORDER },
              "&:hover fieldset": { borderColor: BORDER },
              "&.Mui-focused fieldset": { borderColor: BORDER, borderWidth: 1 },
            },
            "& .MuiSelect-select": {
              fontSize: 12,
              color: "#374151",
              fontWeight: 400,
              padding: "8px 12px",
            },
          }}
        >
          <MenuItem value="all" sx={{ fontWeight: 400 }}>
            All Stages
          </MenuItem>
          {stageOptions.map((stage) => (
            <MenuItem key={stage} value={String(stage).toLowerCase()} sx={{ fontWeight: 400 }}>
              {stage}
            </MenuItem>
          ))}
        </TextField>

        <Typography sx={{ mt: 1, fontSize: 11, color: MUTED, fontWeight: 400 }}>
          {resultsText}
        </Typography>
      </Box>

      {/* list */}
      <Box sx={{ px: 4 }}>
        {loading ? (
          <Typography sx={{ py: 2, fontSize: 12, color: MUTED }}>Loading stages…</Typography>
        ) : null}
        {!loading && !items.length ? (
          <Typography sx={{ py: 2, fontSize: 12, color: MUTED }}>No stage records found.</Typography>
        ) : null}
        {!loading && items.map((it, idx) => (
          <React.Fragment key={it.id || idx}>
            <StageItem item={it} onViewDetails={onViewDetails} />
            {idx !== items.length - 1 && <Divider sx={{ borderColor: BORDER }} />}
          </React.Fragment>
        ))}
      </Box>

      {/* footer */}
      <Box
        sx={{
          px: 4,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: `1px solid ${BORDER}`,
          mt: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <IconButton
            size="small"
            onClick={() => onPrev?.()}
            sx={{ p: 0.5, "&:hover": { backgroundColor: SOFT_BG } }}
          >
            <ArrowLeft2 size={18} color={MUTED} variant="Linear" />
          </IconButton>

          <IconButton
            size="small"
            onClick={() => onNext?.()}
            sx={{ p: 0.5, "&:hover": { backgroundColor: SOFT_BG } }}
          >
            <ArrowRight2 size={18} color={PURPLE} variant="Linear" />
          </IconButton>
        </Box>

        <Typography
          onClick={() => onViewAll?.()}
          sx={{
            fontSize: 12,
            color: PURPLE,
            fontWeight: 400,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          View all Results
        </Typography>
      </Box>
    </Box>
  );
}
